import React, { useMemo } from 'react';

import { AutoAwesome as AutoAwesomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Chip,
  Divider,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import type { CountryStats, StatsResult } from '~/types/attractionTypes';

import { coverageColor } from './attractionUtils';

function SectionLabel({ children }: { children: string }) {
  return (
    <Typography
      variant="caption"
      color="text.disabled"
      sx={{ fontSize: '0.625rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}
    >
      {children}
    </Typography>
  );
}

function Stat({
  label,
  value,
  color,
  tooltip,
}: {
  label: string;
  value: string | number;
  color?: string;
  tooltip?: string;
}) {
  const content = (
    <Box sx={{ textAlign: 'center', minWidth: 80, px: 1 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: color ?? 'text.primary', lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement="top" arrow>
        {content}
      </Tooltip>
    );
  }
  return content;
}

interface InsightItem {
  text: string;
  color: string;
}

function buildGlobalInsights(stats: StatsResult): InsightItem[] {
  const insights: InsightItem[] = [];

  const worst = [...stats.countries]
    .filter((c) => c.attractionCount >= 5)
    .sort((a, b) => a.coverageRate - b.coverageRate)[0];
  if (worst && worst.coverageRate < 50) {
    const uncov = worst.attractionCount - worst.coveredCount;
    insights.push({
      text: `${worst.country} has ${worst.coverageRate}% coverage (${uncov} attractions without offers)`,
      color: 'error.main',
    });
  }

  const mostUncovered = [...stats.countries]
    .map((c) => ({ ...c, uncovered: c.attractionCount - c.coveredCount }))
    .filter((c) => c.uncovered > 0)
    .sort((a, b) => b.uncovered - a.uncovered)[0];
  if (mostUncovered && (!worst || mostUncovered.country !== worst.country)) {
    insights.push({
      text: `${mostUncovered.country} has the most gaps: ${mostUncovered.uncovered} attractions without offers`,
      color: 'warning.main',
    });
  }

  const best = [...stats.countries]
    .filter((c) => c.attractionCount >= 5)
    .sort((a, b) => b.coverageRate - a.coverageRate)[0];
  if (best) {
    insights.push({
      text: `${best.country} leads with ${best.coverageRate}% coverage across ${best.attractionCount} attractions`,
      color: 'success.main',
    });
  }

  const top3 = [...stats.countries].sort((a, b) => b.attractionCount - a.attractionCount).slice(0, 3);
  if (top3.length >= 3 && stats.totalAttractions > 0) {
    const top3Total = top3.reduce((sum, c) => sum + c.attractionCount, 0);
    const pct = Math.round((top3Total / stats.totalAttractions) * 100);
    insights.push({
      text: `Top 3 markets (${top3.map((c) => c.country).join(', ')}) represent ${pct}% of all attractions`,
      color: 'info.main',
    });
  }

  if (insights.length < 4) {
    const uncovered = stats.totalAttractions - stats.totalCovered;
    if (uncovered > 0) {
      insights.push({
        text: `${uncovered} of ${stats.totalAttractions.toLocaleString()} attractions still need offer links`,
        color: 'text.secondary',
      });
    }
  }

  return insights.slice(0, 4);
}

function buildCountryInsights(country: CountryStats): InsightItem[] {
  const insights: InsightItem[] = [];
  const cities = [...(country.cities ?? [])];
  const uncovered = country.attractionCount - country.coveredCount;

  if (country.coverageRate === 100) {
    insights.push({
      text: `${country.country} has full coverage: all ${country.attractionCount} attractions linked to offers`,
      color: 'success.main',
    });
  } else if (uncovered > 0) {
    insights.push({
      text: `${uncovered} of ${country.attractionCount} attractions in ${country.country} still need offer links`,
      color: uncovered > 5 ? 'error.main' : 'warning.main',
    });
  }

  const worstCity = cities.filter((c) => c.attractionCount >= 3).sort((a, b) => a.coverageRate - b.coverageRate)[0];
  if (worstCity && worstCity.coverageRate < 70) {
    const cityUncov = worstCity.attractionCount - worstCity.coveredCount;
    insights.push({
      text: `${worstCity.city} needs attention: ${worstCity.coverageRate}% coverage (${cityUncov} without offers)`,
      color: 'warning.main',
    });
  }

  const bestCity = cities.filter((c) => c.attractionCount >= 3).sort((a, b) => b.coverageRate - a.coverageRate)[0];
  if (bestCity && (!worstCity || bestCity.city !== worstCity.city)) {
    insights.push({
      text: `${bestCity.city} leads with ${bestCity.coverageRate}% coverage across ${bestCity.attractionCount} attractions`,
      color: 'success.main',
    });
  }

  if (cities.length >= 3) {
    const topCity = [...cities].sort((a, b) => b.totalOffers - a.totalOffers)[0];
    if (topCity && country.totalOffers > 0) {
      const pct = Math.round((topCity.totalOffers / country.totalOffers) * 100);
      insights.push({
        text: `${topCity.city} concentrates ${pct}% of all offer links (${topCity.totalOffers.toLocaleString()})`,
        color: 'info.main',
      });
    }
  }

  if (insights.length < 4) {
    insights.push({
      text: `${country.attractionCount} attractions across ${country.cityCount} ${
        country.cityCount === 1 ? 'city' : 'cities'
      } with ${country.totalOffers.toLocaleString()} offer links`,
      color: 'text.secondary',
    });
  }

  return insights.slice(0, 4);
}

interface AttractionsDashboardProps {
  stats: StatsResult;
  countries: Array<{ country: string; countryCode: string }>;
  drillCountryCode: string | null;
  syncLabel?: string;
  onDrillCountryChange: (code: string | null) => void;
  onCountryClick: (country: string) => void;
  onCityClick: (country: string, city: string) => void;
}

function AttractionsDashboard({
  stats,
  countries,
  drillCountryCode,
  syncLabel = 'Synced daily at 6:00 AM AEST',
  onDrillCountryChange,
  onCountryClick,
  onCityClick,
}: AttractionsDashboardProps) {
  const theme = useTheme();

  const covColor = coverageColor(stats.coverageRate);

  const allCountries = useMemo(
    () => [...stats.countries].sort((a, b) => b.attractionCount - a.attractionCount),
    [stats.countries],
  );

  const drillCountry = useMemo(
    () => (drillCountryCode ? allCountries.find((c) => c.countryCode === drillCountryCode) ?? null : null),
    [drillCountryCode, allCountries],
  );

  const insights = useMemo(
    () => (drillCountry ? buildCountryInsights(drillCountry) : buildGlobalInsights(stats)),
    [stats, drillCountry],
  );

  const drillCities = useMemo(() => {
    if (!drillCountry?.cities) return [];
    return [...drillCountry.cities].sort((a, b) => b.attractionCount - a.attractionCount);
  }, [drillCountry]);

  const offerRankingItems = useMemo(() => {
    if (drillCountry?.cities?.length) {
      return [...drillCountry.cities]
        .sort((a, b) => b.totalOffers - a.totalOffers)
        .slice(0, 6)
        .map((c) => ({ label: c.city, totalOffers: c.totalOffers, coverageRate: c.coverageRate }));
    }
    return [...stats.countries]
      .sort((a, b) => b.totalOffers - a.totalOffers)
      .slice(0, 6)
      .map((c) => ({ label: c.country, totalOffers: c.totalOffers, coverageRate: c.coverageRate }));
  }, [stats.countries, drillCountry]);

  return (
    <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(
            theme.palette.info.main,
            0.02,
          )} 100%)`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesomeIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Attraction Intelligence
            </Typography>
            <Chip
              label="AI"
              size="small"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'primary.main', color: '#fff' }}
            />
          </Stack>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
            {syncLabel}
          </Typography>
        </Box>
      </Box>

      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ py: 1.5, px: 1 }}
        justifyContent="space-around"
      >
        <Tooltip
          title={`${stats.totalCovered.toLocaleString()} covered of ${stats.totalAttractions.toLocaleString()} total attractions`}
          placement="top"
          arrow
        >
          <Box sx={{ textAlign: 'center', minWidth: 100, px: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              <Box component="span" sx={{ color: theme.palette[covColor].main }}>
                {stats.totalCovered.toLocaleString()}
              </Box>
              <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.85rem' }}>
                {' '}
                / {stats.totalAttractions.toLocaleString()}
              </Box>
            </Typography>
            <Box sx={{ mt: 0.5, mx: 'auto', maxWidth: 90 }}>
              <Box
                sx={{
                  height: 5,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette[covColor].main, 0.12),
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${stats.coverageRate}%`,
                    borderRadius: 3,
                    bgcolor: theme.palette[covColor].main,
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                      animation: 'shimmer 2s ease-in-out infinite',
                      '@keyframes shimmer': {
                        '0%': { transform: 'translateX(-100%)' },
                        '100%': { transform: 'translateX(200%)' },
                      },
                    }}
                  />
                </Box>
              </Box>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  color: theme.palette[covColor].main,
                  mt: 0.25,
                  display: 'block',
                }}
              >
                {stats.coverageRate}% covered
              </Typography>
            </Box>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', fontSize: '0.6rem', lineHeight: 1.2 }}
            >
              {stats.totalCountries} markets
            </Typography>
          </Box>
        </Tooltip>
        <Stat
          label="Uncovered"
          value={(stats.totalAttractions - stats.totalCovered).toLocaleString()}
          color={
            stats.totalAttractions - stats.totalCovered > 0 ? theme.palette.error.main : theme.palette.success.main
          }
          tooltip="Attractions without any offer linked (need attention)"
        />
        <Stat
          label="Offer Links"
          value={stats.totalOffers.toLocaleString()}
          tooltip="Total offer-attraction proximity matches"
        />
        <Stat label="Cities" value={String(stats.totalCities)} tooltip="Cities with at least one attraction" />
        <Stat label="Markets" value={String(stats.totalCountries)} tooltip="Countries with at least one attraction" />
      </Stack>

      <Divider />

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
        <Box sx={{ px: 2, py: 1.5, borderRight: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.75 }}>
            <AutoAwesomeIcon sx={{ fontSize: 12, color: 'primary.main' }} />
            <SectionLabel>AI Insights</SectionLabel>
          </Stack>
          <Stack spacing={0.75}>
            {insights.map((insight, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: insight.color, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5, color: 'text.secondary' }}>
                  {insight.text}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" alignItems="baseline" spacing={0.75}>
            <SectionLabel>
              {drillCountry ? `${drillCountry.country} Cities by Offers` : 'Top Markets by Offers'}
            </SectionLabel>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
              {(drillCountry?.totalOffers ?? stats.totalOffers).toLocaleString()} links
            </Typography>
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {offerRankingItems.map((item) => {
              const maxOffers = offerRankingItems[0]?.totalOffers || 1;
              const pct = (item.totalOffers / maxOffers) * 100;
              const color = coverageColor(item.coverageRate);
              const palette = theme.palette[color].main;
              return (
                <Stack key={item.label} direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" sx={{ fontWeight: 500, minWidth: 90, fontSize: '0.75rem' }}>
                    {item.label}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                      flex: 1,
                      height: 5,
                      borderRadius: 3,
                      bgcolor: alpha(palette, 0.1),
                      '& .MuiLinearProgress-bar': { bgcolor: palette, borderRadius: 3 },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: palette, minWidth: 45, textAlign: 'right', fontSize: '0.7rem' }}
                  >
                    {item.totalOffers.toLocaleString()}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
          {!drillCountry ? (
            <>
              <SectionLabel>Coverage by Market</SectionLabel>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>
                {stats.totalCountries} countries
              </Typography>
            </>
          ) : (
            <Breadcrumbs separator={<NavigateNextIcon sx={{ fontSize: 14 }} />} sx={{ fontSize: '0.75rem' }}>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                color="primary"
                onClick={() => onDrillCountryChange(null)}
                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
              >
                All Countries
              </Link>
              <Link
                component="button"
                variant="caption"
                underline="hover"
                color="primary"
                onClick={() => {
                  const match = countries.find((co) => co.countryCode === drillCountry.countryCode);
                  if (match) onCountryClick(match.country);
                }}
                sx={{ fontSize: '0.75rem', fontWeight: 600 }}
              >
                {drillCountry.country}
              </Link>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                {drillCountry.cityCount} {drillCountry.cityCount === 1 ? 'city' : 'cities'}
              </Typography>
            </Breadcrumbs>
          )}
        </Stack>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
          {!drillCountry
            ? allCountries.map((c) => {
                const color = coverageColor(c.coverageRate);
                const palette = theme.palette[color];
                const fillPct = Math.min(c.coverageRate, 100);
                return (
                  <Tooltip
                    key={c.countryCode}
                    title={`${c.coveredCount}/${c.attractionCount} covered (${
                      c.coverageRate
                    }%) · ${c.totalOffers.toLocaleString()} proximity links · ${c.cityCount} ${
                      c.cityCount === 1 ? 'city' : 'cities'
                    }`}
                  >
                    <Box
                      data-testid={`country-card-${c.countryCode}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: alpha(palette.main, 0.25),
                        background: `linear-gradient(to right, ${alpha(palette.main, 0.1)} ${fillPct}%, ${alpha(
                          palette.main,
                          0.02,
                        )} ${fillPct}%)`,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: palette.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(palette.main, 0.15)}`,
                        },
                      }}
                      onClick={() => onDrillCountryChange(c.countryCode)}
                    >
                      <Box sx={{ flex: 1, mr: 1.5 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}
                        >
                          {c.country}
                        </Typography>
                        <Typography variant="caption" noWrap color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {c.coveredCount}/{c.attractionCount} · {c.totalOffers.toLocaleString()} links
                        </Typography>
                      </Box>
                      <Chip
                        label={`${c.coverageRate}%`}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 28,
                          borderRadius: '14px',
                          bgcolor: palette.main,
                          color: '#fff',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })
            : drillCities.map((city) => {
                const color = coverageColor(city.coverageRate);
                const palette = theme.palette[color];
                const fillPct = Math.min(city.coverageRate, 100);
                return (
                  <Tooltip
                    key={city.city}
                    title={`${city.coveredCount}/${city.attractionCount} covered (${
                      city.coverageRate
                    }%) · ${city.totalOffers.toLocaleString()} proximity links`}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: alpha(palette.main, 0.25),
                        background: `linear-gradient(to right, ${alpha(palette.main, 0.1)} ${fillPct}%, ${alpha(
                          palette.main,
                          0.02,
                        )} ${fillPct}%)`,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: palette.main,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 2px 8px ${alpha(palette.main, 0.15)}`,
                        },
                      }}
                      onClick={() => {
                        const match = countries.find((co) => co.countryCode === drillCountry.countryCode);
                        if (match) onCityClick(match.country, city.city);
                      }}
                    >
                      <Box sx={{ flex: 1, mr: 1.5 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}
                        >
                          {city.city}
                        </Typography>
                        <Typography variant="caption" noWrap color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {city.coveredCount}/{city.attractionCount} · {city.totalOffers.toLocaleString()} links
                        </Typography>
                      </Box>
                      <Chip
                        label={`${city.coverageRate}%`}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          height: 28,
                          borderRadius: '14px',
                          bgcolor: palette.main,
                          color: '#fff',
                          '& .MuiChip-label': { px: 1 },
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
        </Box>
      </Box>
    </Paper>
  );
}

export default React.memo(AttractionsDashboard);
